import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { gamificationService } from '../../../lib/gamificationService';

const TeamFormation = ({ challenges, userId, onRefresh }) => {
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [joining, setJoining] = useState(false);

  const handleJoinTeamChallenge = async (challengeId) => {
    if (!teamName?.trim()) {
      alert('Please enter a team name');
      return;
    }

    setJoining(true);
    try {
      const { error } = await gamificationService?.joinChallenge(challengeId, userId, teamName);
      if (!error) {
        alert(`Successfully joined challenge with team: ${teamName}`);
        setTeamName('');
        setSelectedChallenge(null);
        onRefresh?.();
      } else {
        alert('Failed to join challenge. You may already be participating.');
      }
    } catch (error) {
      console.error('Error joining team challenge:', error);
      alert('An error occurred while joining the challenge.');
    } finally {
      setJoining(false);
    }
  };

  if (!challenges || challenges?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <Icon name="Users" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Team Challenges Available</h3>
        <p className="text-gray-600">Check back soon for team-based challenges!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-green-100 p-3 rounded-lg">
            <Icon name="Users" className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Team Formation</h3>
            <p className="text-sm text-gray-600">Join or create teams for collaborative challenges</p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">How Team Challenges Work:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-800">
                <li>Create or join a team with a unique team name</li>
                <li>Team members work together toward a common goal</li>
                <li>Progress is aggregated across all team members</li>
                <li>Teams compete for prizes and recognition</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {challenges?.map((challenge) => (
          <div key={challenge?.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <Icon name="Users" className="w-3 h-3 inline mr-1" />
                      TEAM CHALLENGE
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{challenge?.title}</h3>
                  <p className="text-gray-600 mt-1 line-clamp-2">{challenge?.description}</p>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Team Goal:</span>
                  <span className="font-semibold text-gray-900">
                    {challenge?.goal_value} {challenge?.goal_type?.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Team Reward:</span>
                  <span className="font-semibold text-orange-600">
                    {challenge?.points_reward} points per member
                  </span>
                </div>
                {challenge?.prize_description && (
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Gift" className="w-4 h-4 text-purple-600" />
                    <span className="text-purple-600 font-medium">{challenge?.prize_description}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Current Teams:</span>
                  <span className="font-semibold text-gray-900">
                    {challenge?.current_participants || 0} participants
                  </span>
                </div>
              </div>

              <Button
                onClick={() => setSelectedChallenge(challenge)}
                className="w-full"
              >
                <Icon name="UserPlus" className="w-4 h-4 mr-2" />
                Join with Team
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Team Formation Modal */}
      {selectedChallenge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Join Team Challenge</h2>
                <button
                  onClick={() => {
                    setSelectedChallenge(null);
                    setTeamName('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{selectedChallenge?.title}</h3>
                  <p className="text-sm text-gray-600">{selectedChallenge?.description}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Users" className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900">Team Information</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Enter a team name to create a new team or join an existing team with the same name.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team Name *
                  </label>
                  <Input
                    value={teamName}
                    onChange={(e) => setTeamName(e?.target?.value)}
                    placeholder="e.g., Fitness Warriors, Team Alpha"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Choose a unique name or use an existing team name to join them
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => {
                      setSelectedChallenge(null);
                      setTeamName('');
                    }}
                    className="flex-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleJoinTeamChallenge(selectedChallenge?.id)}
                    disabled={joining || !teamName?.trim()}
                    className="flex-1"
                  >
                    {joining ? (
                      <>
                        <Icon name="Loader" className="w-4 h-4 animate-spin mr-2" />
                        Joining...
                      </>
                    ) : (
                      <>
                        <Icon name="UserPlus" className="w-4 h-4 mr-2" />
                        Join Challenge
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamFormation;